/**
 * ProfessionalCalculation API Service
 *
 * Type-safe API functions for professional calculation operations.
 *
 * Business Rules:
 * - ONE calculation per user per year
 * - Users UPDATE existing calculations, don't create duplicates
 * - Always check for existing calculation before creating
 */

import { getCurrentUser } from 'aws-amplify/auth';
import { getAmplifyClient } from '../amplifyClient';

// Lazy client getter - ensures Amplify is configured before accessing models
const client = {
  get models() {
    return getAmplifyClient().models;
  }
};

/**
 * ProfessionalCalculation Response Type
 */
export interface ProfessionalCalculation {
  id: string;
  userId: string;
  calculationYear: number;
  monthlyConsumption: any; // JSON array[12]
  tariffPerMonth: any; // JSON { january: "G11", ... }
  hourlyPatternPerMonth: any; // JSON { january: "UNIFORM", ... }
  valuesFromBasic?: any; // JSON - system 4 values (a)
  valuesFromInvoice?: any; // JSON - system 4 values (b)
  valuesOverridden?: any; // JSON - system 4 values (c)
  valuesFinal?: any; // JSON - system 4 values (d)
  simulationResults?: any; // JSON - all 6 tariff simulations
  recommendedTariff?: string; // "G12", "G12w", etc.
  potentialSavings?: number; // PLN/year
  savedCalculationId?: string; // Link to Basic Calculator
  version: number;
  createdAt: string;
  updatedAt: string;
  owner: string;
}

/**
 * Get current authenticated user ID
 */
async function getCurrentUserId(): Promise<string> {
  try {
    const user = await getCurrentUser();
    return user.userId;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[ProfessionalCalculation] getCurrentUserId error:', error);
    }
    throw new Error('User not authenticated');
  }
}

/**
 * Get user's professional calculation for a specific year
 *
 * @param userId - User ID
 * @param year - Calculation year (e.g., 2025)
 * @returns Professional calculation or null
 */
export async function getProfessionalCalculationByYear(
  userId: string,
  year: number
): Promise<ProfessionalCalculation | null> {
  try {
    // Query using GSI: professionalCalculationsByUserAndYear
    const { data, errors } = await client.models.ProfessionalCalculation.list({
      filter: {
        userId: { eq: userId },
        calculationYear: { eq: year },
      },
    });

    if (errors && errors.length > 0) {
      throw new Error(`GraphQL query failed: ${errors.map((e) => e.message).join(', ')}`);
    }

    // Should only return one result (one-per-year constraint)
    if (data && data.length > 0) {
      return data[0] as unknown as ProfessionalCalculation;
    }

    return null;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[ProfessionalCalculation] getProfessionalCalculationByYear error:', error);
      // Log additional error details if available
      if (error && typeof error === 'object' && 'errors' in error) {
        console.error('[ProfessionalCalculation] GraphQL errors:', (error as { errors: unknown }).errors);
      }
    }
    throw error;
  }
}

/**
 * Get user's professional calculation for current year
 *
 * Convenience method for most common use case
 *
 * @returns Professional calculation or null
 */
export async function getCurrentYearProfessionalCalculation(): Promise<ProfessionalCalculation | null> {
  const userId = await getCurrentUserId();
  const currentYear = new Date().getFullYear();
  return getProfessionalCalculationByYear(userId, currentYear);
}

/**
 * List all professional calculations for current user
 *
 * @returns Array of professional calculations (sorted by year desc)
 */
export async function listMyProfessionalCalculations(): Promise<ProfessionalCalculation[]> {
  try {
    const userId = await getCurrentUserId();

    const { data, errors } = await client.models.ProfessionalCalculation.list({
      filter: {
        userId: { eq: userId },
      },
    });

    if (errors && errors.length > 0) {
      throw new Error(errors.map((e) => e.message).join(', '));
    }

    if (!data) {
      return [];
    }

    // Sort by year descending (most recent first)
    return (data as unknown as ProfessionalCalculation[]).sort((a, b) => b.calculationYear - a.calculationYear);
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[ProfessionalCalculation] listMyProfessionalCalculations error:', error);
    }
    throw error;
  }
}

/**
 * Delete professional calculation by ID
 *
 * @param id - ProfessionalCalculation ID to delete
 * @returns Success boolean
 */
export async function deleteProfessionalCalculation(id: string): Promise<boolean> {
  try {
    const { errors } = await client.models.ProfessionalCalculation.delete({ id });

    if (errors && errors.length > 0) {
      throw new Error(errors.map((e) => e.message).join(', '));
    }

    return true;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[ProfessionalCalculation] deleteProfessionalCalculation error:', error);
    }
    throw error;
  }
}

/**
 * Delete current year's professional calculation
 *
 * Convenience method for deleting current year's calculation from Dashboard
 *
 * @returns Success boolean
 */
export async function deleteCurrentYearProfessionalCalculation(): Promise<boolean> {
  try {
    const calculation = await getCurrentYearProfessionalCalculation();

    if (!calculation) {
      // No calculation to delete
      return true;
    }

    return await deleteProfessionalCalculation(calculation.id);
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[ProfessionalCalculation] deleteCurrentYearProfessionalCalculation error:', error);
    }
    throw error;
  }
}
